import { twMerge } from '@shared/themes';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import { ResultsSectionLink } from '..';
import type { LotteryName } from '../../../../lottery-purchases/types';

type ResultsSectionNoResultProps = ComponentProps<'div'> & {
  date: Date;
  lottery: LotteryName;
  hideLink?: boolean;
  hideStyling?: boolean;
};

/**
 * Displays a minimal "no result" message and optional link for lottery draw results when expected results is not yet available.
 * Namespace for translations: 'lotteries.results.mini'.
 * Key for translations: 'missing_results'.
 *
 * @param {Date} date - The date for which results are missing.
 * @param {LotteryName} lottery - The identifier of the lottery.
 * @param {boolean} [hideLink] - If true, hides the link to the full results page (default: false).
 * @param {boolean} [hideStyling] - If true, renders the message without default container styling (default: false).
 * @returns {JSX.Element} The rendered component.
 */
function ResultsSectionNoResult({ date, lottery, className, hideLink = false, hideStyling = false, ...props }: ResultsSectionNoResultProps) {
  const t = useTranslations('lotteries.results.mini');
  return (
    <div className={twMerge('flex flex-col gap-6', className)} {...props}>
      <p className={twMerge('paragraph-explanatory', hideStyling ? '' : 'p-4 bg-container rounded-md')}>{t('missing_results', { date })}</p>
      {!hideLink ? <ResultsSectionLink lottery={lottery} /> : null}
    </div>
  );
}

export { ResultsSectionNoResult };
